import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { commitReconcileEdges } from "../services/topicsService";

// Minimal Edge Review panel
// Props may be passed via router state: { concepts: string[], batchId?: string, graphName?: string }
export default function EdgeReview({ concepts: propConcepts, batchId: propBatchId, graphName: propGraphName }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = (location && location.state) || {};

  const concepts = propConcepts || params.concepts || [];
  console.log("🔥 FRONTEND: EdgeReview initial route state =", params);
  const [edges, setEdges] = useState(params.initialEdges || []);
  const [agents, setAgents] = useState(params.initialAgents || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const batchId = propBatchId || params.batchId || "";
  const graphName = propGraphName || params.graphName || "";

  const DEFAULT_REL_TYPES = ["DEPENDS_ON", "PART_OF"];
  const initialRelOptions = useMemo(() => {
    const relSet = new Set(DEFAULT_REL_TYPES);
    (params.initialEdges || []).forEach((edge) => {
      const rel = (edge?.type || "").toString().trim();
      if (rel) relSet.add(rel.toUpperCase());
    });
    return Array.from(relSet);
  }, [params.initialEdges]);

  const [relOptions, setRelOptions] = useState(initialRelOptions);
  const [newSource, setNewSource] = useState("");
  const [newTarget, setNewTarget] = useState("");
  const [newType, setNewType] = useState(initialRelOptions[0] || DEFAULT_REL_TYPES[0]);
  const [manualConcepts, setManualConcepts] = useState([]);
  const [useCustomSource, setUseCustomSource] = useState(false);
  const [useCustomTarget, setUseCustomTarget] = useState(false);
  const [useCustomRelation, setUseCustomRelation] = useState(false);
  const [customRelation, setCustomRelation] = useState("");
  const [newRelationLabel, setNewRelationLabel] = useState("");
  const [scoreThreshold, setScoreThreshold] = useState("");
  const CUSTOM_OPTION = "__CUSTOM__";
  const REL_CUSTOM_OPTION = "__REL_CUSTOM__";

  const conceptOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    const addConcept = (label) => {
      if (!label) return;
      const trimmed = label.trim();
      if (!trimmed) return;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      opts.push(trimmed);
    };
    concepts.forEach(addConcept);
    manualConcepts.forEach(addConcept);
    edges.forEach((edge) => {
      addConcept(edge.source);
      addConcept(edge.target);
    });
    return opts.sort((a, b) => a.localeCompare(b));
  }, [concepts, manualConcepts, edges]);

  const ensureConceptTracked = useCallback(
    (label) => {
      const trimmed = label?.trim();
      if (!trimmed) return;
      const exists =
        conceptOptions.some((c) => c.toLowerCase() === trimmed.toLowerCase()) ||
        manualConcepts.some((c) => c.toLowerCase() === trimmed.toLowerCase());
      if (!exists) {
        setManualConcepts((prev) => [...prev, trimmed]);
      }
    },
    [conceptOptions, manualConcepts]
  );

  const ensureRelationTracked = useCallback((label) => {
    const trimmed = (label || "").trim();
    if (!trimmed) return;
    const key = trimmed.toUpperCase();
    setRelOptions((prev) => {
      if (prev.includes(key)) return prev;
      return [...prev, key];
    });
  }, []);

  useEffect(() => {
    setRelOptions((prev) => {
      const relSet = new Set(prev);
      let changed = false;
      edges.forEach((edge) => {
        const rel = (edge?.type || "").toString().trim();
        if (rel) {
          const key = rel.toUpperCase();
          if (!relSet.has(key)) {
            relSet.add(key);
            changed = true;
          }
        }
      });
      return changed ? Array.from(relSet) : prev;
    });
  }, [edges]);

  const filteredEdges = useMemo(() => {
    const parsed = parseFloat(scoreThreshold);
    return edges
      .map((edge, index) => ({ edge, index }))
      .filter(({ edge }) => {
        if (!Number.isFinite(parsed)) return true;
        const vote = typeof edge.vote === "number" ? edge.vote : parseFloat(edge.vote);
        if (!Number.isFinite(vote)) return false;
        return vote >= parsed;
      });
  }, [edges, scoreThreshold]);

  const swapDirection = (idx) => {
    setEdges((prev) =>
      prev.map((e, i) => (i === idx ? { ...e, source: e.target, target: e.source } : e))
    );
  };

  const changeRelation = (idx, rel) => {
    const normalized = (rel || "").toUpperCase();
    ensureRelationTracked(normalized);
    setEdges((prev) => prev.map((e, i) => (i === idx ? { ...e, type: normalized } : e)));
  };

  const removeEdge = (idx) => {
    setEdges((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAddEdge = () => {
    const src = newSource.trim();
    const tgt = newTarget.trim();
    if (!src || !tgt) {
      setError("Please select both Source and Target.");
      setToast("");
      return;
    }
    const relationInput = useCustomRelation ? customRelation : newType;
    const rel = (relationInput || "").trim();
    if (!rel) {
      setError("Please choose a relation type or add a new one.");
      setToast("");
      return;
    }
    const normalizedRel = rel.toUpperCase().replace(/\s+/g, "_");
    const duplicate = edges.some(
      (edge) =>
        edge.source.toLowerCase() === src.toLowerCase() &&
        edge.target.toLowerCase() === tgt.toLowerCase() &&
        (edge.type || "").toUpperCase() === normalizedRel
    );
    if (duplicate) {
      setError("This relationship already exists.");
      setToast("");
      return;
    }
    ensureRelationTracked(normalizedRel);
    ensureConceptTracked(src);
    ensureConceptTracked(tgt);
    setEdges((prev) => [...prev, { source: src, target: tgt, type: normalizedRel }]);
    setError("");
    setToast("Added to the list. Review or edit it below.");
    setNewSource("");
    setNewTarget("");
    setUseCustomSource(false);
    setUseCustomTarget(false);
    setUseCustomRelation(false);
    setCustomRelation("");
    setNewType(normalizedRel);
  };

  const handleAddRelationType = () => {
    const raw = (newRelationLabel || "").trim();
    if (!raw) {
      setError("Enter a relation name to add.");
      setToast("");
      return;
    }
    const normalized = raw.toUpperCase().replace(/\s+/g, "_");
    setRelOptions((prev) => {
      if (prev.includes(normalized)) {
        setToast(`Relation ${normalized} already exists.`);
        return prev;
      }
      setToast(`Relation ${normalized} added. You can use it below.`);
      return [...prev, normalized];
    });
    setNewRelationLabel("");
    setNewType(normalized);
    setUseCustomRelation(false);
    setCustomRelation("");
    setError("");
  };

  const onCommit = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = { edges, draft: false, concepts };
      if (graphName) payload.graphName = graphName;
      if (batchId) payload.batchId = batchId;
      const rsp = await commitReconcileEdges(payload);
      const finalBatch = rsp.batch_id || batchId || "";
      // Navigate to graph view after success
      navigate(`/knowledge-graph${finalBatch ? `?graph_id=${encodeURIComponent(finalBatch)}` : ""}`);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <h2>Review Suggested Relationships</h2>
      {concepts?.length ? (
        <p>
          Concepts ({concepts.length}): {concepts.slice(0, 8).join(", ")}
          {concepts.length > 8 ? " …" : ""}
        </p>
      ) : (
        <p>No concepts provided.</p>
      )}

      <div
        style={{
          position: "sticky",
          top: 0,
          background: "#fff",
          padding: "12px 0",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <strong>Relations:</strong>
          {relOptions.map((r) => (
            <span
              key={r}
              style={{
                padding: "4px 8px",
                border: "1px solid #ccc",
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              {r}
            </span>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
            <input
              value={newRelationLabel}
              onChange={(e) => {
                setNewRelationLabel(e.target.value);
                setError("");
                setToast("");
              }}
              placeholder="e.g. RELATED_TO"
              style={{ minWidth: 160 }}
            />
            <button type="button" onClick={handleAddRelationType} className="knowledge-graph-button">
              + Add relationship
            </button>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            Score filter
            <input
              type="number"
              step="0.1"
              placeholder="e.g. 1.6"
              value={scoreThreshold}
              onChange={(e) => setScoreThreshold(e.target.value)}
              style={{ width: 100 }}
            />
          </label>
          <span style={{ color: "#555" }}>
            Showing {filteredEdges.length} / {edges.length} relationships
          </span>
        </div>
        <strong>Add / Edit Relationship</strong>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
            Source
            <select
              value={useCustomSource ? CUSTOM_OPTION : newSource}
              onChange={(e) => {
                const val = e.target.value;
                if (val === CUSTOM_OPTION) {
                  setUseCustomSource(true);
                  setNewSource("");
                } else {
                  setUseCustomSource(false);
                  setNewSource(val);
                }
                setError("");
                setToast("");
              }}
              style={{ minWidth: 180 }}
            >
              <option value="">Select source…</option>
              {conceptOptions.map((label) => (
                <option key={`src-${label}`} value={label}>
                  {label}
                </option>
              ))}
              <option value={CUSTOM_OPTION}>+ Custom Source</option>
            </select>
            {useCustomSource && (
              <input
                style={{ marginTop: 6, minWidth: 180 }}
                placeholder="Enter custom source"
                value={newSource}
                onChange={(e) => {
                  setNewSource(e.target.value);
                  setError("");
                  setToast("");
                }}
              />
            )}
          </label>
          <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
            Relation
            <select
              value={useCustomRelation ? REL_CUSTOM_OPTION : newType}
              onChange={(e) => {
                const val = e.target.value;
                if (val === REL_CUSTOM_OPTION) {
                  setUseCustomRelation(true);
                  setCustomRelation("");
                  setNewType("");
                } else {
                  setUseCustomRelation(false);
                  setNewType(val);
                }
                setError("");
                setToast("");
              }}
            >
              <option value="">Select relation…</option>
              {relOptions.map((r) => (
                <option key={`rel-${r}`} value={r}>
                  {r}
                </option>
              ))}
              <option value={REL_CUSTOM_OPTION}>+ Custom relation</option>
            </select>
            {useCustomRelation && (
              <input
                style={{ marginTop: 6 }}
                placeholder="Enter relation type"
                value={customRelation}
                onChange={(e) => {
                  setCustomRelation(e.target.value);
                  setError("");
                  setToast("");
                }}
              />
            )}
          </label>
          <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
            Target
            <select
              value={useCustomTarget ? CUSTOM_OPTION : newTarget}
              onChange={(e) => {
                const val = e.target.value;
                if (val === CUSTOM_OPTION) {
                  setUseCustomTarget(true);
                  setNewTarget("");
                } else {
                  setUseCustomTarget(false);
                  setNewTarget(val);
                }
                setError("");
                setToast("");
              }}
              style={{ minWidth: 180 }}
            >
              <option value="">Select target…</option>
              {conceptOptions.map((label) => (
                <option key={`tgt-${label}`} value={label}>
                  {label}
                </option>
              ))}
              <option value={CUSTOM_OPTION}>+ Custom Target</option>
            </select>
            {useCustomTarget && (
              <input
                style={{ marginTop: 6, minWidth: 180 }}
                placeholder="Enter custom target"
                value={newTarget}
                onChange={(e) => {
                  setNewTarget(e.target.value);
                  setError("");
                  setToast("");
                }}
              />
            )}
          </label>
          <button onClick={handleAddEdge} className="knowledge-graph-button" style={{ height: 36 }}>
            + Add
          </button>
          <button style={{ marginLeft: "auto" }} onClick={onCommit} disabled={loading}>
            Commit & Create Subgraph
          </button>
          {error && (
            <span style={{ color: "#b91c1c", fontSize: 12 }}>
              {error}
            </span>
          )}
        </div>
      </div>

      {loading && <p>Loading…</p>}
      {toast && !loading && (
        <p style={{ color: "#166534", fontSize: 12 }}>{toast}</p>
      )}

      <table style={{ width: "100%", marginTop: 12, borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>Source</th>
            <th style={{ textAlign: "left" }}>Relation</th>
            <th style={{ textAlign: "left" }}>Target</th>
            <th>Vote</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredEdges.map(({ edge, index }) => (
            <tr key={`${edge.source}-${edge.target}-${index}`}>
              <td>{edge.source}</td>
              <td>
                <select value={edge.type} onChange={(ev) => changeRelation(index, ev.target.value)}>
                  {relOptions.map((r) => (
                    <option key={`rel-table-${r}`} value={r}>
                      {r}
                    </option>
                  ))}
                  {!relOptions.includes(edge.type) && (
                    <option value={edge.type}>{edge.type}</option>
                  )}
                </select>
              </td>
              <td>{edge.target}</td>
              <td style={{ textAlign: "center" }}>{typeof edge.vote === "number" ? edge.vote.toFixed(2) : ""}</td>
              <td style={{ textAlign: "center" }}>
                <button onClick={() => swapDirection(index)}>Swap</button>
                <button onClick={() => removeEdge(index)} style={{ marginLeft: 8 }}>
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {agents?.length ? <p style={{ marginTop: 8 }}>Agents: {agents.join(", ")}</p> : null}
    </div>
  );
}
